import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import { Box, Accordion, AccordionSummary, Typography, AccordionDetails, Divider, Tooltip, Stack, styled, CircularProgress, Button } from "@mui/material";
import { cloneElement, FC, ReactElement, useMemo, useState } from "react";
import ClassIcon from '@mui/icons-material/Class';
import EditAttributesIcon from '@mui/icons-material/EditAttributes';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PasswordIcon from '@mui/icons-material/Password';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { CRUD_GET_LIST, useEditContext, useGetList } from "ra-core";
import { EncryptionSettingChangesResponseDto, FieldChanged, FieldWithEncryptorInfo, HintEntry } from "@/gen/api/encrypt";
import { MaskingRule, SecondaryClassificationResponseDto } from "@/gen/api/governance";
import { dataTypeDisplayInfo } from "@/components/governance/fieldEdit/maskingRuleDataTypesMap";
import { pages, sorts } from "@/utils/paramsWithGetMainList";
import { RecPatch } from "@/dataProvider/tools";
import { useESCRenameHintContext } from "./ESCRenameHintContext";
import ESCRenameHintEditor, { PossibleRenameIncidentsContextProvider, usePossibleRenameIncidents } from "./ESCRenameHintEditor";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { MANAGE_DATABASE_OPERATIONS } from "@/constants";



const ChangeViewerTree = styled(SimpleTreeView)(
  ({ theme }) => ({
    maxHeight: 480, 
    flexGrow: 1, 
    overflowY: 'auto', 
    '& div.MuiTreeItem-label': {
      textAlign: 'left',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      '& span.MuiTypography-caption.left': { ml: 'auto' },
    },
    '& li.MuiTreeItem-root': {
      width: 'calc(100% - 16px)',
    }
  })
)


const ESChangeViewer: FC = () => {
  const hasWritePermit = useHasPermit([MANAGE_DATABASE_OPERATIONS])
  const { record, loading } = useEditContext()
  const { data: classifications } = useGetList<SecondaryClassificationResponseDto&RecPatch>('settings/governance/data-classification', pages(), sorts(), {}, { action: CRUD_GET_LIST })
  const { effectiveHints } = useESCRenameHintContext()

  const tableChangeData = useMemo(() => {
    const changeData : EncryptionSettingChangesResponseDto = record as any
    if (!changeData) { 
      return {
        addedTableFields: {} as Record<string, FieldWithEncryptorInfo[]>,
        removedTableFields: {} as Record<string, FieldWithEncryptorInfo[]>,
        changedTableFields: {} as Record<string, FieldChanged[]>,
      }
    }
    const addedTableFields = (changeData.fieldsAdded || []).reduce((acc, added) => {
      const keySchemaTable = `${added.schemaName}-${added.tableName}`
      const bag = acc[keySchemaTable] || (acc[keySchemaTable] = [])
      bag.push(added)
      return acc
    }, {} as Record<string, FieldWithEncryptorInfo[]>)
    const removedTableFields = (changeData.fieldsRemoved || []).reduce((acc, removed) => {
      const keySchemaTable = `${removed.schemaName}-${removed.tableName}`
      const bag = acc[keySchemaTable] || (acc[keySchemaTable] = [])
      bag.push(removed)
      return acc
    }, {} as Record<string, FieldWithEncryptorInfo[]>)
    const changedTableFields = (changeData.fieldsChanged || []).reduce((acc, changed) => {
      const keySchemaTable = `${changed.schemaName}-${changed.tableName}`
      const bag = acc[keySchemaTable] || (acc[keySchemaTable] = [])
      bag.push(changed)
      return acc
    }, {} as Record<string, FieldChanged[]>)

    return {
      addedTableFields,
      removedTableFields,
      changedTableFields,
    }

  }, [record]);

  const possibleRenameIncidents = usePossibleRenameIncidents(tableChangeData, effectiveHints)

  const vizData = useMemo(() => {

    // copy over data to later operate on
    const addedTableFields = Object.keys(tableChangeData.addedTableFields).reduce((acc, key) => ({
      ...acc,
      [key]: tableChangeData.addedTableFields[key].reduce((acc1, field) => ({
        ...acc1,
        [field.fieldName || '']: field,
      }), {} as any),
    }), {} as Record<string, Record<string, FieldWithEncryptorInfo>>)
    const removedTableFields = Object.keys(tableChangeData.removedTableFields).reduce((acc, key) => ({
      ...acc,
      [key]: tableChangeData.removedTableFields[key].reduce((acc1, field) => ({
        ...acc1,
        [field.fieldName || '']: field,
      }), {} as any),
    }), {} as Record<string, Record<string, FieldWithEncryptorInfo>>)
    const changedTableFields = Object.keys(tableChangeData.changedTableFields).reduce((acc, key) => ({
      ...acc,
      [key]: [...tableChangeData.changedTableFields[key]],
    }), {} as Record<string, FieldChanged[]>)
    
    const tableRenameAdjust = {} as Record<string, CUnit>

    // adjust data when taken renameHints into account
    for(const group of Object.values(effectiveHints)) {
      const fst = group.hints[0]
      const keySchemaTable_1 = `${fst.schemaName}-${fst.oldTableName}`
      const keySchemaTable_2 = `${fst.newSchemaName || fst.schemaName}-${fst.newTableName}`
      const aBag = addedTableFields[keySchemaTable_2]
      const rBag = removedTableFields[keySchemaTable_1]
      if (!aBag || !rBag) { continue; }
      
      if (group.renamedOnTable) {
        Object.keys(aBag).forEach(fname => {
          if (rBag[fname]) {
            // fields with same name, then we think they are actually the same field
            const fromFieldInfo = rBag[fname]
            const toFieldInfo = aBag[fname]
            const isEncryptorChanged = 
              fromFieldInfo.encryptor !== toFieldInfo.encryptor
              || displayMaskingRule(fromFieldInfo) !== displayMaskingRule(toFieldInfo) 
              || fromFieldInfo?.secondaryClassificationId !== toFieldInfo?.secondaryClassificationId
            const isSchemaChanged =
              (plainDataTypeString(fromFieldInfo) !== plainDataTypeString(toFieldInfo))
              || fromFieldInfo.isIdentity !== toFieldInfo.isIdentity
              || fromFieldInfo.defaultValue !== toFieldInfo.defaultValue
            if (isEncryptorChanged || isSchemaChanged) {
              const cArr = changedTableFields[keySchemaTable_1] || (changedTableFields[keySchemaTable_1] = [])
              cArr.push({
                schemaName: fst.schemaName,
                tableName: fst.oldTableName,
                fieldName: fname,
                isEncryptorChanged,
                fromFieldInfo,
                toFieldInfo,
              })
            } else if (!tableRenameAdjust[keySchemaTable_1]) {
              // if there is no encryptorChange and schemaChange, we would not show the field in tree view,
              // but we still need to generate a "sampleUnit" in order to show the table has been renamed.
              // so we need to keep an entry in the tableRenameAdjust bag
              tableRenameAdjust[keySchemaTable_1] = { kind: 'change', pair: [fromFieldInfo, toFieldInfo] }
            }
            delete rBag[fname]
            delete aBag[fname]
          }
        })
      }
      group.hints.forEach(hint => {
        if (!hint.oldFieldName || !hint.newFieldName) { return }
        const toFieldInfo = aBag[hint.newFieldName]
        const fromFieldInfo = rBag[hint.oldFieldName]
        if (fromFieldInfo && toFieldInfo) {
          const isEncryptorChanged = 
            fromFieldInfo.encryptor !== toFieldInfo.encryptor
            || displayMaskingRule(fromFieldInfo) !== displayMaskingRule(toFieldInfo) 
            || fromFieldInfo?.secondaryClassificationId !== toFieldInfo?.secondaryClassificationId
          const cArr = changedTableFields[keySchemaTable_1] || (changedTableFields[keySchemaTable_1] = [])
          cArr.push({
            schemaName: fst.schemaName,
            tableName: fst.oldTableName,
            fieldName: hint.oldFieldName,
            isEncryptorChanged,
            fromFieldInfo,
            toFieldInfo,
          })
          delete rBag[hint.oldFieldName]
          delete aBag[hint.newFieldName]
        }
      })
      if (Object.keys(aBag).length === 0) { delete addedTableFields[keySchemaTable_2] }
      if (Object.keys(rBag).length === 0) { delete removedTableFields[keySchemaTable_1] }
    }

    const empty = {
      e: {} as Record<string, TGroupCUnits>, // e for Encryptor
      s: {} as Record<string, TGroupCUnits>, // s for Schema
      eSummary: { add: 0, change: 0 },
      sSummary: { add: 0, remove: 0, change: 0 },
    };

    const assembleTGroupData = (groupBag: Record<string, TGroupCUnits>, key: string, u: CUnit, skipCount = false) => {
      const kind = u.kind
      const [x, y] = u.pair
      const group = groupBag[key] || (groupBag[key] = {
        cunits: [],
        fieldRenamedUnits: {},
        addCount: 0,
        removeCount: 0,
        changeCount: 0,
      } as any)
      !skipCount && group.cunits.push(u)
      
      if (x && y) {
        if (!group.hints) {
          const hints = effectiveHints[`${x.schemaName}!${y.schemaName}!${x.tableName}!${y.tableName}`]
          group.hints = hints
          if (hints) {
            group.isTableRenamed = hints.renamedOnTable
            if (group.isTableRenamed) {
              group.schemaChanged = x.schemaName !== y.schemaName
              group.sampleUnit = u
            }
            group.fieldRenamedUnits = group.cunits.reduce((acc, unit1) => {
              const [x1, y1] = unit1.pair
              if (x1 && y1 && hints.hints.some(h => h.oldFieldName === x1.fieldName && h.newFieldName === y1.fieldName)) {
                acc[(x1 || y1).fieldName || ''] = true
              }
              return acc
            }, {} as Record<string, boolean>)
          }
        } else if (group.hints.hints.some(h => h.oldFieldName === x.fieldName && h.newFieldName === y.fieldName)) {
          group.fieldRenamedUnits[(x || y).fieldName || ''] = true
        }
      }
      if (!group.key) {
        group.key = key
        group.schemaChanged = x?.schemaName !== y?.schemaName
        group.sampleUnit = group.sampleUnit || u
      }
      if (!skipCount) {
        switch(kind) {
          case 'add': { group.addCount = group.addCount + 1; break; }
          case 'change': { group.changeCount = group.changeCount + 1; break; }
          default: { group.removeCount = group.removeCount + 1; break; }
        }
      }
    }

    for(const key of Object.keys(tableRenameAdjust)) {
      const unit = tableRenameAdjust[key]
      assembleTGroupData(empty.s, key, unit, true/* skipCount */)
    }

    const result = [
      ...Object.entries(addedTableFields).flatMap(([key, val]) => Object.values(val).map(x => [
        key,
        { kind: 'add', pair: [x, undefined] } as CUnit,
        !!(x.encryptor || x.maskingRule || x.secondaryClassificationId) // isEncryptorChanged
      ] as const)),
      ...Object.entries(removedTableFields).flatMap(([key, val]) => Object.values(val).map(x => [
        key,
        { kind: 'remove', pair: [x, undefined] } as CUnit,
        false // isEncryptorChanged
      ] as const)),
      ...Object.entries(changedTableFields).flatMap(([key, val]) => val.map(x => [
        key,
        { kind: 'change', pair: [x.fromFieldInfo, x.toFieldInfo] } as CUnit,
        !!x.isEncryptorChanged 
          || displayMaskingRule(x.fromFieldInfo) !== displayMaskingRule(x.toFieldInfo) 
          || x?.fromFieldInfo?.secondaryClassificationId !== x?.toFieldInfo?.secondaryClassificationId
      ] as const))
    
    ].reduce((acc, triple) => {
      const [key, unit, isEncryptorChanged] = triple
      if (isEncryptorChanged) {
        let kind = unit.kind as  'add'|'change'
        let u = unit
        const [x, y] = unit.pair
        if (x && !x.isView) {
          if (unit.kind === 'add') {
            u = {kind: 'add', pair: [undefined, x || y] as any}
          } else if (unit.kind === 'change') {
            if (!x.encryptor && !!y?.encryptor) {
              kind = 'add'
              u = {kind: 'add', pair: unit.pair}
            }
          }
          assembleTGroupData(acc.e, key, u)

          acc.eSummary[kind] = acc.eSummary[kind] + 1
        }
      }

      if (unit.kind === 'change') {
        const [x, y] = unit.pair
        if (x.fieldName === y?.fieldName && (isEncryptorChanged // maybe only encryptor changed
          || x.secondaryClassificationId !== y?.secondaryClassificationId) // or maybe classification changed but no others

        // we would only show 4 item changes in DB Schema list: isPrimaryKey dataType, isIdentity, defaultValue
        // then if none of these 4 has changed, skip adding to vizData
        && (plainDataTypeString(x) === plainDataTypeString(y))
        && x.isIdentity === y?.isIdentity
        && x.defaultValue === y?.defaultValue
        && x.isPrimaryKey === y?.isPrimaryKey
        ) {
          return acc
        }
      }
      assembleTGroupData(acc.s, key, unit)

      acc.sSummary[unit.kind] = acc.sSummary[unit.kind] + 1
      return acc
    }, empty)
    
    sortVizData(result.e)
    sortVizData(result.s)
    return result
  }, [effectiveHints, tableChangeData]);

  const [openEditRenames, setOpenEditRenames] = useState(false)

  if (loading || !record) { 
    return (
      <Stack direction="row" justifyContent="center">
        <CircularProgress />
      </Stack>
    ) 
  }
  const changeData : EncryptionSettingChangesResponseDto = record as any

  if (!changeData.isChanged) {
    return (
      <Stack direction="row" justifyContent="center" >
        <Typography variant="body1">No Change</Typography>
      </Stack>
    )
  }

  return (
    <>
      <Box>
        {changeData.encryptionKeyChange?.isChanged &&
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel-encryptionKey-content"
              id="panel-encryptionKey-header"
            >
              <Typography sx={{ mr: 'auto' }}>Encryption Key</Typography>
              <Typography variant="caption" color="error" sx={{ textDecoration: 'line-through' }}>{changeData.encryptionKeyChange.fromName}</Typography>
              <Typography variant="caption" color="success.main" sx={{ mx: 2 }}>{changeData.encryptionKeyChange.toName}</Typography>
            </AccordionSummary>
          </Accordion>
        }
        {(vizData.eSummary.add + vizData.eSummary.change) > 0 &&
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel-encryptionRules-content"
              id="panel-encryptionRules-header"
            >
              <Typography sx={{ mr: 'auto' }}>Encryption Rules</Typography>
              <Typography variant="caption" color="success.main">
                {vizData.eSummary.add} <i>added</i></Typography>
              <Typography variant="caption" color="warning.main" sx={{ mx: 2 }}>
                {vizData.eSummary.change} <i>modified</i></Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Divider sx={{ mb: 1 }} />
              <ChangeViewerTree
                aria-label="encryption rule change navigator"
                slots={{
                  collapseIcon: ExpandMoreIcon,
                  expandIcon: ChevronRightIcon,
                }}
              >
                {Object.keys(vizData.e).sort().map(key => {
                  const tableData = vizData.e[key]
                  const [first, second] = tableData.sampleUnit.pair
                  const fOrS = first || second

                  return (
                    <TreeItem key={key} itemId={key} label={
                      <>
                        {tableData.isTableRenamed
                          ? <>
                              <Typography variant="caption" color="InactiveCaptionText" sx={{ mr: 1, textDecoration: tableData.schemaChanged ? 'line-through' : undefined }}>{first.schemaName}.</Typography>{" "}
                              <Typography variant="body2" color="error.light" sx={{ textDecoration: 'line-through' }}>
                                {first.tableName}
                              </Typography>
                              <Typography variant="body2" color="success.light" sx={{ ml: 1 }}>
                                {tableData.schemaChanged &&
                                  <Typography variant="caption" color="InactiveCaptionText" sx={{ mr: 1 }}>{second?.schemaName}.</Typography>}{" "}
                                {second?.tableName}
                              </Typography>
                            </>
                          : <>
                              <Typography variant="caption" color="InactiveCaptionText" sx={{ mr: 1 }}>{fOrS.schemaName}.</Typography> 
                              {fOrS.isView ? <i>{fOrS.tableName}</i> : fOrS.tableName}
                            </>
                        }
                        <Box sx={{ ml: 'auto'}} />
                        <Typography variant="caption" sx={{ mx: 1 }} color="success.light" className="left">{tableData.addCount}</Typography>
                        <Typography color="InactiveCaptionText" >/</Typography>
                        <Typography variant="caption" sx={{ mx: 1 }} color="warning.main">{tableData.changeCount}</Typography>
                      </>
                    }>
                      {tableData.cunits.map(unit => {
                        const [x, y] = unit.pair
                        const color = 
                            unit.kind === 'add'    ? 'success.main'
                          : unit.kind === 'change' ? 'warning.main'
                          : 'error';
                        const fieldName = (x || y).fieldName
                        const isFieldRenamed = tableData.fieldRenamedUnits[fieldName || '']
                        const [mr_x, mr_y] = [displayMaskingRule(x), displayMaskingRule(y)]

                        const pieces = [
                          ((x?.secondaryClassificationId || '') !== (y?.secondaryClassificationId || '')) && classifications &&
                            { title: 'Classification', color, Icon: <ClassIcon/>, 
                              values: [x?.secondaryClassificationId, y?.secondaryClassificationId].map(sid => {
                                if (!sid || !classifications[sid]) { return undefined }
                                const clss = classifications[sid]
                                return `${clss.primaryClassificationText} - ${clss.name}`
                              }) },
                          ((x?.encryptionRuleName || '') !== (y?.encryptionRuleName || '')) && 
                            { title: 'Encryption Rule', color, Icon: <VerifiedUserIcon/>, values: [x?.encryptionRuleName, y?.encryptionRuleName] },
                          mr_x !== mr_y && 
                            { title: 'Masking Rule', color, Icon: <PasswordIcon/>, values: [mr_x, mr_y] },
                        ].filter(p => !!p)
                        const itemKey = `${key}-${fieldName}`
                        return (
                          <TreeItem key={itemKey} itemId={itemKey} label={
                            <>
                              {isFieldRenamed
                                ? <>
                                    <Typography color="error.light" sx={{ textDecoration: 'line-through' }}>{x?.fieldName}</Typography>
                                    <Typography color="success.light" sx={{ ml: 1 }}>{y?.fieldName}</Typography>
                                  </>
                                : <Typography color={color === 'error' ? 'warning.main' : color}>{fieldName}</Typography>
                              }
                              <Box sx={{ml:'auto'}} />
                              {pieces.map((p, i) => 
                                <ERulePiece key={i} {...(p as any)} end={i === pieces.length - 1} />
                              )}
                            </>
                          } />
                        )
                      })}
                    </TreeItem>
                  )
                })}
              </ChangeViewerTree>
            </AccordionDetails>
          </Accordion>
        }
        {((vizData.sSummary.add + vizData.sSummary.remove + vizData.sSummary.change) > 0 
        || Object.keys(vizData.s).length > 0) &&
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel-dbSchema-content"
              id="panel-dbSchema-header"
            >
              <Typography sx={{ mr: 4 }}>Database Schema</Typography>
              {(Object.keys(effectiveHints).length > 0
              || possibleRenameIncidents.hasPossibleIncidents) && hasWritePermit &&
                <Button startIcon={<EditAttributesIcon />} sx={{ mt: -0.5 }}
                  onClick={(e) => { e.stopPropagation(); setOpenEditRenames(true) }}
                >
                  Help Hint Renames
                </Button>
              }
              <Box sx={{ ml: 'auto' }} />
              <Typography variant="caption" color="success.main">
                {vizData.sSummary.add} <i>added</i> </Typography>
              <Typography variant="caption" color="error" sx={{ mx: 2 }}>
                {vizData.sSummary.remove} <i>removed</i> </Typography>
              <Typography variant="caption" color="warning.main" sx={{ mr: 2 }}>
                {vizData.sSummary.change} <i>modified</i> </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Divider sx={{ mb: 1 }} />
              <ChangeViewerTree
                aria-label="database schema change navigator"
                slots={{
                  collapseIcon: ExpandMoreIcon,
                  expandIcon: ChevronRightIcon,
                }}
              >
                {Object.keys(vizData.s).sort().map(key => {
                  const tableData = vizData.s[key]
                  const [first, second] = tableData.sampleUnit.pair

                  return (
                    <TreeItem key={key} itemId={key} label={
                      <>
                        {tableData.isTableRenamed
                          ? <>
                              <Typography variant="caption" color="InactiveCaptionText" sx={{ mr: 1, textDecoration: tableData.schemaChanged ? 'line-through' : undefined }}>{first.schemaName}.</Typography>{" "}
                              <Typography variant="body2" color="error.light" sx={{ textDecoration: 'line-through' }}>
                                {first.tableName}
                              </Typography>
                              <Typography variant="body2" color="success.light" sx={{ ml: 1 }}>
                                {tableData.schemaChanged &&
                                  <Typography variant="caption" color="InactiveCaptionText" sx={{ mr: 1 }}>{second?.schemaName}.</Typography>}{" "}
                                {second?.tableName}
                              </Typography>
                            </>
                          : <>
                              <Typography variant="caption" color="InactiveCaptionText" sx={{ mr: 1 }}>{first.schemaName}.</Typography> 
                              {first.isView ? <i>{first.tableName}</i> : first.tableName}
                            </>
                        }
                        <Box sx={{ ml: 'auto'}} />
                        <Typography variant="caption" sx={{ mx: 1 }} color="success.light" className="left">{tableData.addCount}</Typography>
                        <Typography color="InactiveCaptionText" >/</Typography>
                        <Typography variant="caption" sx={{ mx: 1 }} color="error">{tableData.removeCount}</Typography>
                        <Typography color="InactiveCaptionText" >/</Typography>
                        <Typography variant="caption" sx={{ mx: 1 }} color="warning.main">{tableData.changeCount}</Typography>
                      </>
                    }>
                      {tableData.cunits.map(unit => {
                        const [x, y] = unit.pair
                        const color = 
                            unit.kind === 'add'    ? 'success.main'
                          : unit.kind === 'remove' ? 'error'
                          : unit.kind === 'change' ? 'warning.main'
                          : '';
                        const isFieldRenamed = tableData.fieldRenamedUnits[x.fieldName || '']

                        const xDataTypeStr = plainDataTypeString(x) || ''
                        const yDataTypeStr = plainDataTypeString(y);
                        
                        const pieces = [
                          xDataTypeStr !== yDataTypeStr && 
                            { title: 'DataType',     color, values: [xDataTypeStr, yDataTypeStr] },
                          (!y || x.isPrimaryKey !== y.isPrimaryKey) && (x.isPrimaryKey || y?.isPrimaryKey) && 
                            { title: 'IsPrimaryKey',   color, values: [`${x.isPrimaryKey}`, y && y.isPrimaryKey !== undefined ? `${y.isPrimaryKey}` : undefined] },
                          (!y || x.isIdentity !== y.isIdentity) && (x.isIdentity || y?.isIdentity) && 
                            { title: 'IsIdentity',   color, values: [`${x.isIdentity}`, y && y.isIdentity !== undefined ? `${y.isIdentity}` : undefined] },
                          (!y || x.defaultValue !== y.defaultValue) && 
                            { title: 'DefaultValue', color, values: [x.defaultValue || '""', y?.defaultValue] }
                        ].filter(d => !!d)
                        const fieldName = (x || y).fieldName
                        const itemKey = `${key}-${fieldName}`
                        return (
                          <TreeItem key={itemKey} itemId={itemKey} label={
                            <>
                              {isFieldRenamed
                                ? <>
                                    <Typography color="error.light" sx={{ textDecoration: 'line-through' }}>{x?.fieldName}</Typography>
                                    <Typography color="success.light" sx={{ ml: 1 }}>{y?.fieldName}</Typography>
                                  </>
                                : <Typography color={color}>{fieldName}</Typography>
                              }
                              <Box sx={{ml:'auto'}} />
                              {pieces.map((p, i) => (
                                <SchemaChangePiece key={i} {...(p as any)} end={i === pieces.length - 1} />
                              ))}
                            </>
                          } />
                        )
                      })}
                    </TreeItem>
                  )
                })}
              </ChangeViewerTree>
            </AccordionDetails>
          </Accordion>
        }
      </Box>
      {hasWritePermit &&
        <PossibleRenameIncidentsContextProvider value={possibleRenameIncidents}>
          <ESCRenameHintEditor open={openEditRenames} setOpen={setOpenEditRenames} />
        </PossibleRenameIncidentsContextProvider>}
    </>
  
  );
}

export default ESChangeViewer;

const Text : FC<{color: any, lineThrough?: boolean}> = ({color, children, lineThrough}) => {
  return (
    <Typography 
      variant="caption" 
      sx={{ mx: 0.5, lineHeight: '14px', 
        ...(!!lineThrough &&
          {textDecoration: 'line-through'}) 
      }} 
      color={color}
    >
      {children}
    </Typography>
  )
}


const ERulePiece : FC<{color:string, title:string, values: [string, string|undefined], Icon: ReactElement, end: boolean}> = ({
  color,
  title,
  values,
  Icon,
  end,
}) => {
  const [x,y] = values
  return (
    <>
      <Tooltip title={title}>
        <Stack direction="row" alignItems="center">
          {cloneElement(Icon, {transform:"scale(0.5)", color:"disabled"})}
          {!!x && <Text color="error" lineThrough={true}>{x}</Text>}
          {!!y && <Text color={x ? 'warning.main' : 'success.main'}>{y}</Text>}
        </Stack>
      </Tooltip>
      {!end && <Divider sx={{ borderRightWidth: 1, height: 14, mx: 2 }} />}
    </>
  )
}

const SchemaChangePiece : FC<{color:string, title:string, values: [string, string|undefined], end: boolean}> = ({
  color,
  title,
  values,
  end,
}) => {
  const [x,y] = values
  return (
    <>
      <Stack direction="row" alignItems="center">
        <Typography variant="caption" color="InactiveCaptionText"><i>{title}:</i></Typography>
        <Text color={y ? 'error' : color} lineThrough={!!y || color === 'error'}>{x}</Text>
        {!!y && <Text color={color}>{y}</Text>}
      </Stack>
      {!end && <Divider sx={{ borderRightWidth: 1, height: 14, mx: 2 }} />}
    </>
  )
}

const displayMaskingRule = (x?: FieldWithEncryptorInfo) => {
  if (!x) { return null }
  return x.maskingRule === MaskingRule.ReplaceWithDefaultDate ? x.maskingValue
    : x.maskingRule    === MaskingRule.ReplaceWithCharacter   ? ''.padStart(9, x.maskingValue || '')
    : x.maskingRule    === MaskingRule.ReplaceWithCharacterPreserveLastFour ? '1234'.padStart(9, x.maskingValue || '')
    : null
}

const plainDataTypeString = (x?: FieldWithEncryptorInfo) => {
  if (!x) { return undefined }
  const info = dataTypeDisplayInfo(x);
  return `${info.dataType}${info.dataTypeExt ? info.dataTypeExt : ''}`
}

const cmpCKind = (a: ChangeKind, b: ChangeKind) => {
  const [a1, b1] = [a, b].map(x => 
      x === 'add'    ? 0
    : x === 'remove' ? 1
    : x === 'change' ? 2
    : 0)
  return a1 - b1
}
const sortVizData = (x:Record<string, TGroupCUnits>) => {
  for(const k in x) {
    const arr = x[k].cunits
    arr.sort((a, b) => {
      const k = cmpCKind(a.kind, b.kind)
      return k !== 0 ? k : (
        ((a.pair[0] || a.pair[1]).fieldName || '') < ((b.pair[0] || b.pair[1]).fieldName || '') ? -1 : 1
      )
    })
  }
}

type ChangeKind = 'add' | 'remove' | 'change'
type CPair = [FieldWithEncryptorInfo, FieldWithEncryptorInfo|undefined]
interface CUnit {
  kind: ChangeKind,
  pair: CPair,
}

interface TGroupCUnits {
  cunits: CUnit[],
  sampleUnit: CUnit,
  key: string,
  isTableRenamed: boolean,
  schemaChanged: boolean,
  addCount: number,
  removeCount: number,
  changeCount: number,
  fieldRenamedUnits: Record<string, boolean>,
  hints: { renamedOnTable: boolean; hints: HintEntry[]; } | undefined,
}
